import { Head, useForm, Link } from '@inertiajs/react'
import { useState } from 'react'
import Input from '../../components/ui/Input'
import Button from '../../components/ui/Button'

/**
 * SignUp page - User registration
 * Route: GET /sign-up
 * Form submits to: POST /sign-up
 *
 * Creates a user and automatically creates an Agency for them
 */
export default function SignUp() {
  const { data, setData, post, processing, errors } = useForm({
    email: '',
    password: '',
    password_confirmation: '',
    agency_name: '',
    phone: '',
    city: '',
    accept_terms: false
  })

  const [passwordStrength, setPasswordStrength] = useState('')

  const handlePasswordChange = (value) => {
    setData('password', value)

    // Calculate password strength
    if (value.length === 0) {
      setPasswordStrength('')
    } else if (value.length < 8) {
      setPasswordStrength('weak')
    } else if (value.length < 12 && /[A-Z]/.test(value) && /[0-9]/.test(value)) {
      setPasswordStrength('medium')
    } else if (value.length >= 12 && /[A-Z]/.test(value) && /[0-9]/.test(value) && /[^A-Za-z0-9]/.test(value)) {
      setPasswordStrength('strong')
    } else {
      setPasswordStrength('medium')
    }
  }

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak': return 'bg-red-500'
      case 'medium': return 'bg-yellow-500'
      case 'strong': return 'bg-green-500'
      default: return 'bg-gray-300'
    }
  }

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 'weak': return 'Faible'
      case 'medium': return 'Moyen'
      case 'strong': return 'Fort'
      default: return ''
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    post('/sign-up')
  }

  return (
    <>
      <Head title="Créer un compte" />

      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Créer votre compte
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Commencez à générer des leads qualifiés
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              {/* Agency Information */}
              <div className="space-y-4 pb-4 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Votre agence
                </h3>

                <Input
                  label="Nom de l'agence"
                  type="text"
                  name="agency_name"
                  value={data.agency_name}
                  onChange={(e) => setData('agency_name', e.target.value)}
                  error={errors.agency_name}
                  placeholder="Agence Immobilière..."
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="Téléphone"
                    type="tel"
                    name="phone"
                    value={data.phone}
                    onChange={(e) => setData('phone', e.target.value)}
                    error={errors.phone}
                    placeholder="06 12 34 56 78"
                  />

                  <Input
                    label="Ville"
                    type="text"
                    name="city"
                    value={data.city}
                    onChange={(e) => setData('city', e.target.value)}
                    error={errors.city}
                    placeholder="Paris"
                  />
                </div>
              </div>

              {/* User Information */}
              <div className="space-y-4 pt-2">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Vos identifiants
                </h3>

                <Input
                  label="Email"
                  type="email"
                  name="email"
                  value={data.email}
                  onChange={(e) => setData('email', e.target.value)}
                  error={errors.email}
                  required
                  autoFocus
                  placeholder="vous@example.com"
                />

                <div>
                  <Input
                    label="Mot de passe"
                    type="password"
                    name="password"
                    value={data.password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    error={errors.password}
                    required
                    placeholder="••••••••"
                  />

                  {/* Password strength indicator */}
                  {passwordStrength && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                            style={{
                              width: passwordStrength === 'weak' ? '33%' :
                                     passwordStrength === 'medium' ? '66%' : '100%'
                            }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-600">
                          {getPasswordStrengthText()}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Utilisez 12+ caractères avec majuscules, chiffres et symboles
                      </p>
                    </div>
                  )}
                </div>

                <Input
                  label="Confirmer le mot de passe"
                  type="password"
                  name="password_confirmation"
                  value={data.password_confirmation}
                  onChange={(e) => setData('password_confirmation', e.target.value)}
                  error={errors.password_confirmation}
                  required
                  placeholder="••••••••"
                />
              </div>

              {/* Terms acceptance */}
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="accept_terms"
                    name="accept_terms"
                    type="checkbox"
                    checked={data.accept_terms}
                    onChange={(e) => setData('accept_terms', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="accept_terms" className="text-gray-600">
                    J'accepte les{' '}
                    <a href="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
                      conditions générales d'utilisation
                    </a>
                    {' '}et la{' '}
                    <a href="/privacy" className="text-primary-600 hover:text-primary-700 font-medium">
                      politique de confidentialité
                    </a>
                  </label>
                </div>
              </div>

              {errors.accept_terms && (
                <p className="text-sm text-red-600 -mt-2">
                  {errors.accept_terms}
                </p>
              )}
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={processing || !data.accept_terms}
              loading={processing}
            >
              Créer mon compte
            </Button>

            {/* Sign in link */}
            <div className="text-center">
              <span className="text-sm text-gray-600">
                Déjà un compte ?{' '}
                <Link
                  href="/sign-in"
                  className="font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Se connecter
                </Link>
              </span>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
